import React from 'react';
import { Link } from 'gatsby';
import { Container, Row, Column, List } from '@pella/sash-framework';
import { CTA } from '../../';
import { useFooter } from '../../utilities/static-queries/footer';
import { useBranchContext } from '../../../context/BranchContext';
import { useOktaAuth } from '../../../hooks/use-okta-auth';
import { useBranchId } from '../../../hooks/use-branch-id';
import { useBranchData } from '../../../hooks/use-branch-data';
import { isValidArray } from '../../utilities/validations';

import Logo from '../logo';
import Socials from './socials';

const Footer = () => {
	const data = useFooter();
	const { profile } = useOktaAuth();
	const { currentBranch } = useBranchContext();
	const branchId = useBranchId();
	const branchData = useBranchData();
	const ctaType = profile.isPro ? 'pro_ctas' : 'ho_ctas';

	return (
		<footer
			id='footer'
			className={`footer bg-black [&_a]:no-underline`}
		>
			<Container>
				<div className={`footerTop grid grid-cols-[50px_1fr] place-content-center items-center gap-x-6 py-8 md:grid-cols-1 xl:grid-cols-[82px_1fr]`}>
					<div className='col-start-1 row-start-1 text-center md:mb-6 xl:mb-0 xl:text-left'>
						<Link
							to={profile.isPro ? '/professionals/' : '/'}
							title='Link opens pella.com homepage'
							className='footerTop-logoLink block'
						>
							<Logo className='footerTop-logo block h-24 w-full' />
						</Link>
					</div>

					{isValidArray(data?.top_row?.[ctaType]) && (
						<List
							bullets='none'
							className={`footerTop-${ctaType} col-start-2 row-start-1 flex flex-row flex-wrap md:col-start-1 md:row-start-2 md:justify-center xl:col-start-2 xl:row-start-1 xl:justify-start`}
						>
							{data.top_row[ctaType].map((cta, index) => (
								<li
									key={index}
									className={`footerTop-li-${index} w-6/12 md:mx-6 md:w-auto`}
								>
									<CTA
										data={cta}
										className={`footerTop-cta-${index} font-semibold text-white hover:!text-neutral-400`}
									/>
								</li>
							))}
						</List>
					)}
				</div>

				<div className={`footerMiddle grid auto-rows-[minmax(min-content,max-content)] grid-cols-1 xl:grid-cols-[265px_1fr] xl:gap-x-[90px]`}>
					{isValidArray(data?.box_cta?.[ctaType]) && (
						<List
							bullets='none'
							className={`footerMiddleBox-links md:-mx-1 md:flex md:flex-row md:flex-wrap xl:mx-0 xl:block`}
						>
							{data.box_cta[ctaType].map((cta, index) => {
								if (cta?.link?.href === '/where-to-buy/schedule-consultation/') {
									if (currentBranch?.hide_rta) return null;
									else if (branchData?.find((data) => data.branch_retail?.includes(branchId))?.hide_rta) return null;
								}

								return (
									<li
										key={index}
										className={`footerMiddleBox-li-${index} md:w-4/12 md:px-1 xl:w-full xl:px-0`}
									>
										<CTA
											data={cta}
											className={`footerMiddleBox-link-${index} mb-2 inline-flex w-full items-center rounded-sm border border-neutral-600 bg-neutral-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-neutral-600 focus:bg-neutral-600 md:mb-0 md:py-4 xl:mb-2 [&>span]:mr-2.5 [&>span]:text-primary`}
										/>
									</li>
								);
							})}
						</List>
					)}

					{isValidArray(data?.standard_columns) && (
						<Row className='footerColumns mt-4 md:justify-center xl:mt-0 xl:justify-start'>
							{data.standard_columns.map((col, index) => (
								<Column
									key={index}
									className={`footerColumn-${index} my-4 max-w-full flex-12/12 text-center md:mx-6 md:flex-[0_0_165px] md:text-left xl:flex-[0_0_200px]`}
								>
									{col.heading && <p className='footerColumn-heading mb-2 uppercase tracking-3px text-white md:mb-5'>{col.heading}</p>}
									{isValidArray(col?.[ctaType]) && (
										<List
											bullets='none'
											className='footerColumn-links'
										>
											{col[ctaType].map((cta, colIndex) => {
												const careersLink = currentBranch?.local_nav?.find((item) => item.link.title === 'Careers');

												if (cta?.link?.href === '/support/financing/') {
													if (currentBranch?.hide_financing) return null;
													else if (branchData?.find((data) => data.branch_retail?.includes(branchId))?.hide_financing) return null;
												}

												return (
													<li
														key={colIndex}
														className={`footerColumn-li-${colIndex} mb-2 md:mb-4`}
													>
														<CTA
															data={cta.link?.title === 'Careers' && careersLink ? careersLink : cta}
															className={`footerColumn-cta-${colIndex} text-xs text-white hover:text-neutral-400 focus:text-neutral-400 xl:text-sm`}
														/>
													</li>
												);
											})}
										</List>
									)}
								</Column>
							))}
						</Row>
					)}
				</div>

				<div className={`footerBottom grid grid-cols-1 items-center gap-y-6 pb-7 pt-10 md:grid-cols-[1fr_85px] md:gap-y-2.5 md:pt-7 xl:grid-cols-[1fr_max-content_85px] xl:gap-x-12 xl:pt-12`}>
					<div
						data-silktide='ignore'
						className='be-ix-link-block col-start-1 row-start-3 md:col-span-2 md:row-start-2 xl:col-span-1 xl:row-start-1'
					/>

					{isValidArray(data?.social?.ctas) && (
						<Socials
							data={data.social.ctas}
							className='footerBottom-socials col-start-1 row-start-1 xl:col-start-2'
						/>
					)}
					{isValidArray(data?.warranty) && (
						<div className='footerBottom-warranty col-start-1 row-start-2 text-center md:col-start-2 md:row-start-1 xl:col-start-3'>
							<CTA
								data={data.warranty[0]}
								renderAs='link'
								className='footerBottom-warranty-cta inline-block [&>span]:block [&_svg]:h-[69px] [&_svg]:w-[85px]'
							/>
						</div>
					)}
				</div>
			</Container>

			{data?.copyright && (
				<div className='footerCopyright bg-neutral-900 py-4 md:py-5'>
					<Container>
						<div className={`flex flex-col-reverse items-center xl:flex-row`}>
							{data.copyright.heading && (
								<p className='footerCopyright-copy mt-2 text-center text-xs text-white md:mx-auto md:mt-3 md:w-6/12 xl:mx-0 xl:mt-0 xl:text-left'>
									&#169; {new Date().getFullYear()} {data.copyright.heading}
								</p>
							)}

							{isValidArray(data.copyright.ctas) && (
								<List
									bullets='none'
									className='footerCopyright-links flex flex-row flex-wrap justify-center text-center md:mx-auto md:w-6/12 xl:mx-0 xl:justify-end xl:text-right'
								>
									{data.copyright.ctas.map((cta, index) => (
										<li
											key={index}
											className={`footerCopyright-li-${index} w-full md:mx-2 md:w-auto xl:ml-6 xl:mr-0`}
										>
											<CTA
												data={cta}
												className={`footerCopyright-cta-${index} text-xs text-neutral-400 hover:text-neutral-200`}
											/>
										</li>
									))}
								</List>
							)}
						</div>
					</Container>
				</div>
			)}
		</footer>
	);
};

export default Footer;
